import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from 'react';
import { progressPersistence } from '../utils/progressPersistence';

// Defines the structure for a single job's progress, accommodating all microservices
export interface JobProgress {
  jobId: string;
  status: string;
  stage?: string; // Job stage from backend
  phase?: string;
  downloadProgress?: number; // Standardized progress field from backend (was estimatedProgress)
  subtitleProgress?: number; // Progress for subtitle/transcription generation (0-100)
  progress?: number; // Generic progress from transcription/clipping
  processedFragments?: number;
  totalFragments?: number;
  downloadedSize?: string;
  downloadRate?: string;
  elapsedTime?: number;
  displayText?: string;
  currentStageDetail?: string; // Can be an alias for displayText
  detail?: string; // Alternative display text field
  transcriptionRate?: string; // Transcription processing rate
  isIndeterminate?: boolean;
  isDownloadActuallyCompleted?: boolean;
  clipsProcessedCount?: number;
  clipsTotalCount?: number;
  
  // Additional backend fields
  activeConnections?: number;
  completedConnections?: number;
  overallProgress?: number;
  throughput?: {
    processingRate?: string;
    downloadSpeed?: string;
    itemsProcessed?: number;
    itemsTotal?: number;
  };
  
  // Metadata and timing
  subProgress?: number; // From progress consumer
  estimatedTimeRemaining?: number;
  
  // Progress validation and consistency
  _lastValidProgress?: number; // Track last valid progress to prevent going backward
  _progressDirection?: 'forward' | 'backward'; // Track progress direction
  
  timestamp: number;
}

// Defines the structure for the context's state
interface JobProgressState {
  [jobId: string]: JobProgress;
}

// Defines the shape of the context value
interface JobProgressContextType {
  progress: JobProgressState;
  updateJobProgress: (jobId: string, data: JobProgress, source?: 'socket' | 'api') => void;
  getJobProgress: (jobId: string) => JobProgress | undefined;
  refreshProgressFromServer: (jobId: string) => Promise<void>;
  isLoaded: boolean;
  // Enterprise SSE
  isEnterpriseConnected: boolean;
  connectEnterpriseSSE: (jobIds: string[], userId: number) => void;
  disconnectEnterpriseSSE: () => void;
}

// Create the context with an undefined default value
const JobProgressContext = createContext<JobProgressContextType | undefined>(undefined);

// Custom hook for easy context consumption
export const useJobProgress = () => {
  const context = useContext(JobProgressContext);
  if (!context) {
    throw new Error('useJobProgress must be used within a JobProgressProvider');
  }
  return context;
};

// Provider component that will wrap the application
export const JobProgressProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [progress, setProgress] = useState<JobProgressState>({});
  const [isLoaded, setIsLoaded] = useState(false);
  
  // 🚀 Enterprise SSE Connection State
  const [isEnterpriseConnected, setIsEnterpriseConnected] = useState(false);
  const enterpriseSSERef = useRef<EventSource | null>(null);
  const activeJobIdsRef = useRef<Set<string>>(new Set());
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load persisted progress on mount
  useEffect(() => {
    const loadPersistedProgress = async () => {
      try {
        // Clean up old localStorage format
        try {
          const oldData = localStorage.getItem('jobProgress');
          if (oldData) {
            console.log('[JobProgressContext] Cleaning up old localStorage progress format');
            localStorage.removeItem('jobProgress');
          }
        } catch (e) {
          // Ignore localStorage cleanup errors
        }

        const persistedProgress = await progressPersistence.loadAllProgress();
        console.log('[JobProgressContext] Loaded persisted progress:', Object.keys(persistedProgress).length, 'jobs');
        setProgress(persistedProgress);
      } catch (error) {
        console.warn('[JobProgressContext] Failed to load persisted progress:', error);
      } finally {
        setIsLoaded(true);
      }
    };

    loadPersistedProgress();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (enterpriseSSERef.current) {
        enterpriseSSERef.current.close();
        enterpriseSSERef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      activeJobIdsRef.current.clear();
      setIsEnterpriseConnected(false);
    };
  }, []); // No dependencies needed since refs don't change

  // Progress validation helper - enhanced for subtitle progress specifically
  const validateProgressUpdate = useCallback((jobId: string, newData: JobProgress): JobProgress => {
    const existingProgress = progress[jobId];
    
    if (!existingProgress) {
      return { 
        ...newData, 
        downloadProgress: newData.downloadProgress ?? 0,
        subtitleProgress: newData.subtitleProgress ?? 0,
        progress: newData.progress ?? 0,
        _lastValidProgress: newData.downloadProgress || newData.progress || 0 
      };
    }
    
    // Define valid stage order for progress validation
    const stageOrder = [
      'PENDING', 'PROCESSING', 'DOWNLOADING_VOD', 'FETCHING_CHAT', 
      'GENERATING_SUBTITLES', 'DETECTING_HIGHLIGHTS', 'CLIPPING_HIGHLIGHTS', 
      'SAVING_FILES', 'COMPLETED', 'FAILED', 'CANCELLED', 'RE_CLIPPING'
    ];
    
    const isValidStageTransition = (fromStage: string, toStage: string): boolean => {
      // Be more permissive - allow all transitions for now to fix reversion issues
      return true;
      
      // Original logic kept for reference:
      // Terminal states
      // if (toStage === 'COMPLETED' || toStage === 'FAILED' || toStage === 'CANCELLED') return true;
      // Allow forward progression
      // const fromIndex = stageOrder.indexOf(fromStage);
      // const toIndex = stageOrder.indexOf(toStage);
      // if (fromIndex === -1 || toIndex === -1) return true; // Unknown stages, allow
      // Allow forward movement or same stage
      // return toIndex >= fromIndex || toStage === fromStage;
    };
    
    // Check if this is a legitimate stage transition
    const isStageTransition = newData.status !== existingProgress.status || newData.stage !== existingProgress.stage;
    const isValidTransition = isStageTransition ? isValidStageTransition(existingProgress.status, newData.status) : true;
    
    if (isStageTransition && !isValidTransition) {
      console.warn(`[JobProgressContext] Invalid stage transition for ${jobId}: ${existingProgress.status} -> ${newData.status}, keeping existing state`);
      // Keep existing state for invalid transitions
      return existingProgress;
    }
    
    // Validate progress fields - enhanced for subtitle progress specifically
    const validateField = (fieldName: keyof JobProgress, newValue?: number) => {
      if (newValue === undefined) return newValue;
      
      const oldValue = existingProgress[fieldName] as number;
      
      // Always allow progress updates for valid stage transitions
      if (isStageTransition && isValidTransition) {
        return newValue;
      }
      
      // Special handling for subtitle progress during GENERATING_SUBTITLES stage
      if (fieldName === 'subtitleProgress' && newData.status === 'GENERATING_SUBTITLES') {
        // Allow subtitle progress updates with more flexibility
        if (newValue >= 0 && newValue <= 100) {
          // Only log every 10% to reduce console spam
          if (Math.floor(newValue / 10) !== Math.floor((oldValue || 0) / 10)) {
            console.log(`[JobProgressContext] Subtitle progress update for ${jobId}: ${Math.floor(newValue)}%`);
          }
          return newValue;
        }
      }
      
      // For same stage updates, allow reasonable variations
      if (oldValue && newValue < oldValue && newValue < 100) {
        const regressionAmount = oldValue - newValue;
        // Increased tolerance from 5% to 20% to handle transcription variations
        if (regressionAmount > 20) {
          console.warn(`[JobProgressContext] Large progress regression for ${jobId}: ${fieldName} ${oldValue}% -> ${newValue}%, keeping old value`);
          return oldValue;
        } else {
          console.log(`[JobProgressContext] Allowing progress adjustment for ${jobId}: ${fieldName} ${oldValue}% -> ${newValue}%`);
          return newValue; // Allow reasonable regressions
        }
      }
      
      return newValue;
    };
    
    const currentProgressValue = newData.downloadProgress || newData.progress || newData.overallProgress || 0;
    const lastValidValue = existingProgress._lastValidProgress || 0;
    
    const validatedData: JobProgress = {
      ...newData,
      downloadProgress: validateField('downloadProgress', newData.downloadProgress) ?? 0,
      subtitleProgress: validateField('subtitleProgress', newData.subtitleProgress) ?? 0,
      progress: validateField('progress', newData.progress) ?? 0,
      downloadedSize: newData.downloadedSize ?? '0.0',
      downloadRate: newData.downloadRate ?? '0.0',
      _lastValidProgress: isValidTransition ? currentProgressValue : Math.max(lastValidValue, currentProgressValue),
      _progressDirection: currentProgressValue > lastValidValue ? 'forward' : 'backward'
    };
    
    // Debug subtitle progress specifically
    if (newData.subtitleProgress !== undefined && newData.subtitleProgress !== validatedData.subtitleProgress) {
      console.warn(`[JobProgressContext] Subtitle progress modified for ${jobId}: ${newData.subtitleProgress}% -> ${validatedData.subtitleProgress}%`);
    }
    
    return validatedData;
  }, [progress]);

  const updateJobProgress = useCallback((jobId: string, data: JobProgress, source: 'socket' | 'api' = 'api') => {
    // Don't update until initial load is complete
    if (!isLoaded) {
      console.warn(`[JobProgressContext] Skipping update for ${jobId} - not loaded yet`);
      return;
    }
    
    // 🚀 PROFESSIONAL PROGRESS MANAGEMENT
    const existingProgress = progress[jobId];
    const now = Date.now();
    
    // Priority system: Socket.IO > API (with recency check)
    if (source === 'socket') {
      // 🚫 Enterprise SSE disabled - only regular Socket.IO updates allowed
      if (!data.status && data.stage) {
        console.warn(`⚠️ [Smart Update] Rejected incomplete Socket.IO update for ${jobId}:`, {
          status: data.status,
          stage: data.stage,
          reason: 'Incomplete data - Enterprise SSE is disabled'
        });
        return;
      }
      
      console.log(`🔥 [Smart Update] Socket.IO update for ${jobId}:`, {
        status: data.status,
        stage: data.stage,
        download: data.downloadProgress,
        subtitle: data.subtitleProgress,
        clips: data.clipsProcessedCount ? `${data.clipsProcessedCount}/${data.clipsTotalCount}` : undefined,
        displayText: data.displayText,
        // DEBUG: Add detailed progress field analysis
        allProgressFields: Object.keys(data).filter(k => k.toLowerCase().includes('progress')),
        rawSubtitleProgress: data.subtitleProgress,
        transcriptionRate: data.transcriptionRate,
        existing: existingProgress?.status
      });
    } else {
      // API update - reject recent updates but be less aggressive since we filter bad Socket.IO data
      if (existingProgress?.timestamp && (now - existingProgress.timestamp) < 3000) {
        // console.log(`⚡ [Smart Update] API rejected - recent data exists for ${jobId} (age: ${now - existingProgress.timestamp}ms), current: ${existingProgress.status}`);
        return;
      }
      
      // Prevent API from reverting to QUEUED when we have active progress
      if (data.status === 'QUEUED' && existingProgress?.status && 
          !['QUEUED', 'PENDING'].includes(existingProgress.status)) {
        console.log(`⚡ [Smart Update] API rejected - preventing QUEUED reversion for ${jobId}: ${existingProgress.status} -> ${data.status}`);
        return;
      }
      
      // Additional validation for API updates
      if (existingProgress && data.downloadProgress && existingProgress.downloadProgress && 
          data.downloadProgress < existingProgress.downloadProgress - 2) {
        console.log(`⚡ [Smart Update] API rejected - progress regression detected for ${jobId}: ${existingProgress.downloadProgress}% -> ${data.downloadProgress}%`);
        return;
      }
      
      // Only log significant updates in development
      if (import.meta.env.DEV) {
        // Log status changes
        if (data.status && data.status !== existingProgress?.status) {
          console.log(`📡 [JobProgress] Status change for ${jobId}: ${existingProgress?.status} → ${data.status}`);
        }
        // Log milestone progress (every 25%) - Commented out to reduce console spam
        /*
        const downloadMilestone = data.downloadProgress && data.downloadProgress % 25 === 0;
        const subtitleMilestone = data.subtitleProgress && data.subtitleProgress % 25 === 0;
        if (downloadMilestone || subtitleMilestone) {
          console.log(`📡 [JobProgress] Progress milestone for ${jobId}:`, {
            download: downloadMilestone ? `${data.downloadProgress}%` : undefined,
            subtitle: subtitleMilestone ? `${data.subtitleProgress}%` : undefined
          });
        }
        */
      }
    }
    
    // Validate and clean progress data
    const validatedData = validateProgressUpdate(jobId, data);
    
    const updatedProgressEntry = { ...validatedData, timestamp: Date.now() };
    
    setProgress(prevProgress => ({
      ...prevProgress,
      [jobId]: updatedProgressEntry,
    }));
    
    // Persist using modern persistence layer (async, non-blocking)
    progressPersistence.saveProgress(jobId, updatedProgressEntry).catch(error => {
      console.warn('[JobProgressContext] Failed to persist progress:', error);
    });
  }, [isLoaded]); // Remove progress dependency to prevent unnecessary re-renders

  const getJobProgress = useCallback((jobId: string) => {
    return progress[jobId];
  }, [progress]);

  // Refresh progress from server for active jobs
  const refreshProgressFromServer = useCallback(async (jobId: string) => {
    try {
      // Use consolidated backend service for progress data
      let serverProgress = null;
      
      try {
        const backendResponse = await fetch(`/api/jobs/${jobId}/progress`);
        if (backendResponse.ok) {
          serverProgress = await backendResponse.json();
          // console.log(`[JobProgressContext] Refreshed progress from backend for job ${jobId}:`, serverProgress);
        }
      } catch (backendError) {
        console.warn(`[JobProgressContext] Backend progress endpoint unavailable:`, backendError);
      }
      
      if (serverProgress) {
        updateJobProgress(jobId, {
          ...serverProgress,
          jobId,
          timestamp: Date.now()
        });
      }
    } catch (error) {
      console.warn(`[JobProgressContext] Failed to refresh progress from server for job ${jobId}:`, error);
    }
  }, [updateJobProgress]);

  // 🚫 Enterprise SSE DISABLED - Phase 1: Fixing flickering by removing competing progress sources
  const connectEnterpriseSSE = useCallback((jobIds: string[], userId: number) => {
    console.log('🚫 [Enterprise SSE] DISABLED - Using Socket.IO only to prevent flickering');
    // Do nothing - Enterprise SSE is disabled
  }, []);

  const disconnectEnterpriseSSE = useCallback(() => {
    console.log('🚫 [Enterprise SSE] DISABLED - Nothing to disconnect');
    // Clean up any existing connections if they exist
    if (enterpriseSSERef.current) {
      enterpriseSSERef.current.close();
      enterpriseSSERef.current = null;
    }
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    activeJobIdsRef.current.clear();
    setIsEnterpriseConnected(false);
  }, []);

  const value = {
    progress,
    updateJobProgress,
    getJobProgress,
    refreshProgressFromServer,
    isLoaded,
    // Enterprise SSE
    isEnterpriseConnected,
    connectEnterpriseSSE,
    disconnectEnterpriseSSE,
  };

  return (
    <JobProgressContext.Provider value={value}>
      {children}
    </JobProgressContext.Provider>
  );
};